"use client";

import React from "react";
import { Loader, Phone } from "lucide-react";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage } from "@/components/ui/form";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { cn } from "@/lib/utils";
import { Link } from "@/components/common/Link";
export default function RequestAQuote() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <section data-section-id="30836" id="schedule-your-inspection" ref={ref} className="bg-secondary relative overflow-hidden py-16 px-6 md:py-32 md:px-12 lg:px-32">
      <div className={cn("mx-auto max-w-4xl transition-all duration-1000 ease-out", isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10")}>
        <div className="text-center mb-16 space-y-6">
          <h2 className="font-serif text-4xl md:text-[56px] leading-tight text-white uppercase tracking-tight">
            Schedule Your Expert Inspection
          </h2>
          <p className="text-primary text-lg md:text-xl font-medium tracking-wide uppercase">
            Professional Property Evaluations You Can Trust
          </p>
        </div>

        <FormProvider formKey="schedule_inspection_form_homeinspectioninhomesteadfl" formId="schedule_inspection_form_homeinspectioninhomesteadfl" sectionName="request-a-quote" defaultValues={{
        name: "",
        email: "",
        phone: "",
        address: "",
        project_details: "",
        preferred_start_date: "",
        budget: ""
      }} validationRules={{
        name: {
          required: true
        },
        email: {
          required: true,
          pattern: "email"
        },
        phone: {
          required: true,
          pattern: "phone"
        },
        project_details: {
          required: true
        },
        budget: {
          min: 500,
          max: 100000
        }
      }} submitText="Schedule Now" successMessage="Thank you! Your inspection request has been received. We will contact you shortly to confirm the time." errorMessage="Something went wrong. Please try again or call us directly." className="w-full">
          <QuoteForm />
        </FormProvider>
      </div>
    </section>;
}
function QuoteForm() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="p-12 bg-white/5 border border-primary/30 rounded-md text-center animate-in fade-in zoom-in duration-500 max-w-2xl mx-auto">
        <h3 className="text-3xl font-serif text-white mb-6">Request Received</h3>
        <p className="text-white/80 mb-8 text-lg">{successMessage}</p>
        <Button onClick={resetForm} variant="outline" className="bg-transparent border-primary text-primary hover:bg-primary hover:text-primary-foreground transition-colors uppercase tracking-wider font-bold rounded-md">
          Send Another Request
        </Button>
      </div>;
  }
  const inputStyles = "border border-transparent bg-white/10 rounded-md px-4 py-4 h-auto text-white placeholder:text-white/40 focus-visible:ring-0 focus-visible:border-primary transition-all duration-400 shadow-none text-lg";
  const labelStyles = "text-white/70 uppercase tracking-widest text-xs font-bold mb-2";
  return <Form onSubmit={handleSubmit} className="space-y-12">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-x-12 gap-y-10">
        <FormField control={control} name="name" rules={{
        required: "Name is required"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className={labelStyles}>Your Full Name</FormLabel>
              <FormControl>
                <Input placeholder="John Doe" className={inputStyles} {...field} />
              </FormControl>
              <FormMessage className="text-destructive mt-2" />
            </FormItem>} />

        <FormField control={control} name="email" rules={{
        required: "Email is required",
        pattern: {
          value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
          message: "Invalid email address"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className={labelStyles}>Email Address</FormLabel>
              <FormControl>
                <Input type="email" placeholder="john@example.com" className={inputStyles} {...field} />
              </FormControl>
              <FormMessage className="text-destructive mt-2" />
            </FormItem>} />

        <FormField control={control} name="phone" rules={{
        required: "Phone number is required"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className={labelStyles}>Phone Number</FormLabel>
              <FormControl>
                <Input type="tel" placeholder="(555) 123-4567" className={inputStyles} {...field} />
              </FormControl>
              <FormMessage className="text-destructive mt-2" />
            </FormItem>} />

        <FormField control={control} name="address" render={({
        field
      }) => <FormItem>
              <FormLabel className={labelStyles}>Property Address</FormLabel>
              <FormControl>
                <Input placeholder="123 Main St, Scranton, PA" className={inputStyles} {...field} />
              </FormControl>
              <FormMessage className="text-destructive mt-2" />
            </FormItem>} />
      </div>

      <FormField control={control} name="project_details" rules={{
      required: "Please describe your project"
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className={labelStyles}>Inspection Details</FormLabel>
            <FormControl>
              <Textarea placeholder="Tell us about the property (size, age, specific concerns)..." className={cn(inputStyles, "min-h-[120px] resize-y")} {...field} />
            </FormControl>
            <FormMessage className="text-destructive mt-2" />
          </FormItem>} />

      <div className="grid grid-cols-1 md:grid-cols-2 gap-x-12 gap-y-10">
        <FormField control={control} name="preferred_start_date" render={({
        field
      }) => <FormItem>
              <FormLabel className={labelStyles}>Preferred Inspection Date</FormLabel>
              <FormControl>
                <Input type="date" className={cn(inputStyles, "block w-full")} {...field} />
              </FormControl>
              <FormMessage className="text-destructive mt-2" />
            </FormItem>} />

        <FormField control={control} name="budget" rules={{
        min: {
          value: 500,
          message: "Minimum square footage is 500"
        },
        max: {
          value: 100000,
          message: "Maximum square footage is 100,000"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className={labelStyles}>Estimated Square Footage</FormLabel>
              <FormControl>
                <Input type="number" placeholder="e.g., 2500" className={inputStyles} {...field} />
              </FormControl>
              <FormMessage className="text-destructive mt-2" />
            </FormItem>} />
      </div>

      {submitError && <div className="p-4 bg-destructive/20 border border-destructive/30 rounded text-destructive-foreground text-sm text-center">
          {submitError}
        </div>}

      <div className="pt-8 flex flex-col md:flex-row items-center justify-center gap-4">
        <Button type="submit" disabled={isSubmitting} className="w-full md:w-auto min-w-[280px] h-16 text-lg font-bold tracking-widest uppercase bg-primary text-primary-foreground hover:bg-primary/90 transition-all duration-300 rounded-md shadow-lg">
          {isSubmitting ? <>
              <Loader className="animate-spin mr-2 size-5" />
              Scheduling...
            </> : "Request Inspection"}
        </Button>
        <Button asChild variant="outline" className="w-full md:w-auto min-w-[280px] h-16 text-lg font-bold tracking-widest uppercase border-2 border-white text-white hover:bg-white hover:text-secondary transition-all duration-300 rounded-md">
          <Link to="tel:786-382-6895">
            <Phone className="mr-2 size-5" />
            Call 786-382-6895
          </Link>
        </Button>
      </div>
    </Form>;
}